/**
 * mad-video — remembered video consent
 *
 *   - Remembered consent is a single cookie: mad_video_consent=1, 60 days.
 *   - Legacy per-provider localStorage flags are migrated on first read.
 *   - madGrantVideoConsent() / madRevokeVideoConsent() are the public API for
 *     cookie banners and consent managers (exposed on window by mad-video.js).
 */

var CONSENT_COOKIE_NAME = 'mad_video_consent';
var CONSENT_COOKIE_DAYS = 60;
var LEGACY_LS_PREFIX    = 'mad_video_consent_';

function hasCookie(name) {
  var cookies = document.cookie.split(';');
  for (var i = 0; i < cookies.length; i++) {
    var pair = cookies[i].trim();
    if (pair === name + '=1') return true;
  }
  return false;
}

function setCookie(name, value, days) {
  var expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie =
    name + '=' + value + '; expires=' + expires.toUTCString() +
    '; path=/; SameSite=Lax';
}

function deleteCookie(name) {
  document.cookie =
    name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
}

function hasConsent() {
  return hasCookie(CONSENT_COOKIE_NAME);
}

function rememberConsent() {
  setCookie(CONSENT_COOKIE_NAME, '1', CONSENT_COOKIE_DAYS);
}

/**
 * Turn a legacy per-provider localStorage flag into the consent cookie.
 * Returns true when consent was found (and migrated).
 */
function migrateLegacyConsent(type) {
  try {
    var legacyKey = LEGACY_LS_PREFIX + type;
    if (localStorage.getItem(legacyKey) === '1') {
      rememberConsent();
      localStorage.removeItem(legacyKey);
      return true;
    }
  } catch (e) {}
  return false;
}

/**
 * Grant video consent from a cookie banner or consent manager.
 *
 * Writes the mad_video_consent cookie (60 days, SameSite=Lax) and notifies
 * all instances on the page. Consent is now a single on/off flag rather than
 * per-provider — the optional `types` argument is accepted for back-compat
 * but ignored.
 *
 * Examples:
 *   madGrantVideoConsent()                        // grant consent
 *   madGrantVideoConsent('youtube')               // same (arg ignored)
 *   madGrantVideoConsent(['youtube', 'vimeo'])    // same (arg ignored)
 */
function madGrantVideoConsent(types) { // eslint-disable-line no-unused-vars
  rememberConsent();

  // Keep the legacy in-memory flag populated for any code that reads it directly.
  window.madVideoConsentGiven = window.madVideoConsentGiven || {};
  ['youtube', 'vimeo', 'hls', 'mp4'].forEach(function (t) {
    window.madVideoConsentGiven[t] = true;
  });

  // Broadcast once — instances accept an omitted `type` as "all".
  document.dispatchEvent(new CustomEvent('madVideoConsentGiven', {
    detail: {},
    bubbles: false,
  }));
}

/**
 * Withdraw video consent from a cookie banner or consent manager.
 *
 * Clears the mad_video_consent cookie plus the legacy per-provider
 * localStorage flags, then tells every instance on the page to destroy its
 * player and show the consent overlay again — no reload required.
 */
function madRevokeVideoConsent() {
  deleteCookie(CONSENT_COOKIE_NAME);

  try {
    ['youtube', 'vimeo', 'hls', 'mp4'].forEach(function (t) {
      localStorage.removeItem(LEGACY_LS_PREFIX + t);
    });
  } catch (e) {}

  window.madVideoConsentGiven = {};

  document.dispatchEvent(new CustomEvent('madVideoConsentRevoked', {
    detail: {},
    bubbles: false,
  }));
}

module.exports = {
  hasConsent: hasConsent,
  rememberConsent: rememberConsent,
  migrateLegacyConsent: migrateLegacyConsent,
  madGrantVideoConsent: madGrantVideoConsent,
  madRevokeVideoConsent: madRevokeVideoConsent,
};
